/* eslint-disable no-unused-vars */
import { useEffect, useState } from "react";

import Dialog from "src/components/core/Dialog";
import Button from "src/components/core/Button";

import {
  Stepper,
  Step,
  StepButton,
  Stack,
  DialogContent,
  DialogActions,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { useSteps } from "src/hooks/useStep";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import useYupValidationResolver from "src/hooks/useYupValidationResolver";

import { useSnackbar } from "notistack";
import { validFileExtensions } from "src/common/file";

import Step1 from "./Step1";
import Step2 from "./Step2";
import Step3 from "./Step3";

import { Green, Grey } from "src/styles/color";

import { useDialog } from "src/context/DialogContext";
import { createMood, updateMood } from "src/api/shopThisMood";

const steps = ["รายละเอียดรูปภาพ", "เพิ่มสินค้า", "อัพโหลด"];

const MAX_PRODUCT_PER_MOOD = 5;
const MAX_STYLES_PER_MOOD = 5;
const MAX_HASHTAGS_PER_MOOD = 5;

const validationSchema = yup.object().shape({
  mood_name: yup.string().required(),
  room: yup.object().required(),
  styles: yup.array().of(yup.object()).min(1).max(5).required(),
  hashtags: yup.array().of(yup.object()).min(1).required(),
  product_choice_moods: yup.array().of(yup.object()).min(1).required(),
  media: yup
    .object({
      image: yup.mixed().required(),
      hotspot: yup
        .array()
        .of(
          yup.object({
            pc_id: yup.object().required(),
            position_x: yup.number().required(),
            position_y: yup.number().required(),
          })
        )
        .min(1)
        .required(),
    })
    .required(),
});

const AddMoodDialog = ({
  initialValue,
  disableRoomField = false,
  onSuccess,
  onClose,
  editable,
}) => {
  const { openDialog } = useDialog();
  const { enqueueSnackbar } = useSnackbar();

  const defaultValues = {
    mood_name: "",
    room: null,
    styles: [],
    hashtags: [],
    product_choice_moods: [],
    media: {
      image: null,
      hotspot: [],
    },
    ...initialValue, // Used to set initial value of `room` when add mood from room page
  };

  const resolver = useYupValidationResolver(validationSchema);
  const {
    formState: { errors: formErrors, isValid, isDirty },
    control: formControl,
    watch,
    trigger,
    getValues,
    setValue: setFormValue,
  } = useForm({
    resolver,
    defaultValues,
  });

  const [formData, setFormData] = useState(defaultValues);

  /**
   * Check if the form errors object lists these fields.
   * If yes, the form status is not yet valid.
   * */
  const isFirstStepValid = ["mood_name", "media.image", "room", "styles"].every(
    (required) => !Object.keys(formErrors).includes(required)
  );

  const isSecondStepValid = [
    "media.hotspot",
    "product_choice_moods",
    "hashtags",
  ].every((required) =>
    Object.keys(formErrors).every((key) => !key.includes(required))
  );

  /**
   * @notice after form value change, then set value to formData state
   */
  useEffect(() => {
    const subscription = watch((values, { name, type }) => {
      setFormData(values);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  /**
   * @notice after formData state change, then trigger form validation
   */
  useEffect(() => {
    trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const { currentStep, goToNextStep, goToPrevStep } = useSteps({
    initialStep: 0,
    maxStep: 2,
  });

  function getStepComponent() {
    switch (currentStep) {
      case 0:
        return (
          <Step1
            disableRoomField={disableRoomField} // in case of open dialog from room mood list page
            onChange={handleChange}
            formData={formData}
            formErrors={formErrors}
          />
        );
      case 1:
        return (
          <Step2
            onChange={handleChange}
            formData={formData}
            formControl={formControl}
            formErrors={formErrors}
          />
        );
      case 2:
        return <Step3 formData={formData} formControl={formControl} />;
      default:
        return null;
    }
  }

  const isAllowedMedia = (file) => {
    const allowedExtensions = ["png", "jpg", "jpeg"];

    if (!validFileExtensions(file, allowedExtensions)) {
      enqueueSnackbar(
        `File type is not allowed. Only accept ${allowedExtensions.join(
          ", "
        )}.`,
        { variant: "error" }
      );
      return false;
    } else if (file.size > 2428800) {
      enqueueSnackbar(`File size is too large. Max file size is 2MB`, {
        variant: "error",
      });
      return false;
    }

    return true;
  };

  const handleChange = async (event, replaceName, replaceValue) => {
    let name =
      typeof replaceName !== "undefined" ? replaceName : event.target.name;
    let value =
      typeof replaceValue !== "undefined" ? replaceValue : event.target.value;

    let path = name.split(".");
    let [key, ...rest] = path;

    let editingKey = key;
    let updated;

    if (key === "mood_name" || key === "room") {
      updated = value;
    } else if (key === "hashtags") {
      if (value.length > MAX_HASHTAGS_PER_MOOD) {
        enqueueSnackbar(
          `Max hashtags per mood is ${MAX_HASHTAGS_PER_MOOD} items`,
          {
            variant: "error",
          }
        );
        return;
      }
      updated = [
        ...value.map((props) => {
          if (typeof props === "string") {
            return { name: props };
          }
          return { name: props.name };
        }),
      ];
    } else if (key === "styles") {
      if (value.length > MAX_STYLES_PER_MOOD) {
        enqueueSnackbar(`Max styles per mood is ${MAX_STYLES_PER_MOOD} items`, {
          variant: "error",
        });
        return;
      }

      updated = [
        ...value.map((props) => {
          if (typeof props === "string") {
            return { name: props };
          }
          return { name: props.name };
        }),
      ];
    } else if (key === "product_choice_moods") {
      if (value.length > MAX_PRODUCT_PER_MOOD) {
        enqueueSnackbar(
          `Max product choice per mood is ${MAX_PRODUCT_PER_MOOD} items`,
          { variant: "error" }
        );

        return;
      }
      updated = value;
    } else if (key === "media") {
      let [secondLvObjKey, secondLvObjIndex] = rest;

      editingKey = `${key}.${secondLvObjKey}`;
      let editingRow = getValues(editingKey);

      if (secondLvObjKey === "image") {
        if (
          typeof value === "object" &&
          value !== null &&
          !isAllowedMedia(value)
        )
          return;

        updated = value;
      } else if (secondLvObjKey === "hotspot") {
        updated = editingRow.map((spot, spotIndex) => {
          return spotIndex === parseInt(secondLvObjIndex)
            ? {
                ...spot,
                pc_id: value, // update nested spot product choice here
              }
            : spot;
        });
      }
    }

    setFormValue(editingKey, updated);
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        product_choice_moods: formData.product_choice_moods.map(
          (item) => item._id
        ),
        media: {
          hotspot: formData.media.hotspot.map((item) => {
            return {
              ...item,
              pc_id: item.pc_id._id,
            };
          }),
        },
        image: formData.media.image,
      };

      const data = new FormData();

      for (const field in payload) {
        if (field === "mood_name" || field === "image") {
          data.append(field, payload[field]);
        } else {
          data.append(field, JSON.stringify(payload[field]));
        }
      }

      !editable ? await createMood(data) : await updateMood(payload._id, data);

      enqueueSnackbar(`${editable ? "บันทึก" : "อัปโหลดภาพ"}สำเร็จ`, {
        variant: "success",
      });

      onSuccess();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Dialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={true}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: 1024,
            maxWidth: "100%",
          },
        },
        "& .MuiDialogContent-root": {
          py: 0,
        },
      }}
    >
      <DialogTitle>
        {editable ? "แก้ไข" : "เพิ่ม"}ภาพมูด
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack alignItems="center">
          <Stepper
            activeStep={currentStep}
            sx={{
              ".MuiStepLabel-root": { display: "grid", maxWidth: "200px" },
              ".MuiStepIcon-root": { margin: "0 auto 12px auto" },
              ".MuiStepIcon-root.Mui-active": {
                color: Green,
              },
              ".MuiStepIcon-root.Mui-completed": {
                color: Green,
              },
              ".MuiStepLabel-label.Mui-disabled": {
                color: Grey,
              },
              width: 500,
              marginBottom: 3,
            }}
            alternativeLabel
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepButton disabled={true} color="inherit">
                  {label}
                </StepButton>
              </Step>
            ))}
          </Stepper>
          {getStepComponent()}
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "center",
        }}
      >
        <Button
          variant="secondary"
          size="large"
          onClick={currentStep === 0 ? onClose : goToPrevStep}
          sx={{
            minWidth: 160,
          }}
        >
          {currentStep === 0 ? "ยกเลิก" : "ย้อนกลับ"}
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={() =>
            currentStep === 2
              ? openDialog({
                  type: "confirm",
                  title: `คุณต้องการ${
                    editable ? "บันทึกข้อมูล" : "อัปโหลดภาพ"
                  }ใช่หรือไม่?`,
                  onConfirm: () => {
                    handleSubmit();
                  },
                  maxWidth: 342,
                })
              : goToNextStep()
          }
          sx={{
            minWidth: 160,
          }}
          disabled={
            currentStep === 0
              ? !isFirstStepValid
              : currentStep === 1
              ? !isSecondStepValid
              : isFirstStepValid && !isSecondStepValid
          }
        >
          {currentStep === 2 ? (editable ? "บันทึก" : "อัพโหลด") : "ต่อไป"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddMoodDialog;
